import { Button, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Spinner, Stack, Text, useToast, VStack } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { adminGetKeyword, adminUpdateKeyword } from "../../../api";
import MiniMedListItem from "../../../components/Medicine/MiniMedListItem";
import { colors } from "../../../styles";
import { IAdminKeywordDetail } from "../../../types";

interface KeywordModalProps {
    isOpen : boolean;
    onClose : () => void;
    keywordId: string;
    
    page: number;
    size: number;
    searchKeyword: string | null;
}

export default function KeywordModal(props : KeywordModalProps) {

    /* query */
    const { data: keywordData, isLoading: isKeywordDataLoading } = useQuery<IAdminKeywordDetail>({
        queryKey: ['adminGetKeyword', props.keywordId],
        queryFn: () => adminGetKeyword({ keywordId: props.keywordId }),
        enabled: !!props.keywordId && props.isOpen,
    });

    /* form */

    interface IModifyKeywordForm {
        modifyKeyword: string;
    }

    const { register, handleSubmit, formState: { errors, isValid }, watch } = useForm<IModifyKeywordForm>({
        mode: "onChange",
    });

    const isModifyKeywordValid = useMemo(() => {
        
        if (!keywordData) return false;

        const inputValue = watch("modifyKeyword");
        const originalValue = keywordData.keywordWithMedicines.keyword;

        // input값이 기존값과 같거나(isValid도 false일 수 있음) 유효하지 않으면 false
        if (!isValid) return false;
        if (inputValue === originalValue || inputValue === undefined || inputValue === null) return false;
        return true;
    }, [isValid, watch("modifyKeyword"), keywordData]);

    /* onSubmit */

    const onSubmit = async (data:IModifyKeywordForm) => {
        await updateKeywordMutation.mutateAsync({ keywordId: props.keywordId, modifyKeyword: data.modifyKeyword });
    }

    
    /* mutation */

    const queryClient = useQueryClient();

    const toast = useToast();

    const updateKeywordMutation = useMutation({
        mutationFn: adminUpdateKeyword,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword', props.keywordId] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords', props.page, props.size, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinesByKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicineByKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetConnection'] });
            queryClient.invalidateQueries({ queryKey: ['adminAutoCompleteKeyword'] });
            props.onClose();
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* render */

    return (
        <Modal motionPreset="slideInBottom" size={"4xl"} isOpen={props.isOpen} onClose={props.onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text color={colors.blue} fontSize="lg" fontWeight="bold">
                        {`#${keywordData?.keywordWithMedicines.keyword ?? "-"}` }
                    </Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody paddingTop={5} as="form" onSubmit={handleSubmit(onSubmit)}>
                    <VStack 
                        padding={5}
                        alignItems={"flex-start"}
                    >
                        <FormControl>
                            <FormLabel fontSize="sm" color={colors.blue}>
                                수정할 키워드   
                            </FormLabel>
                            <Input 
                                {...register("modifyKeyword", {required:true, minLength: 1, maxLength: 20})} 
                                type="text" 
                                defaultValue={keywordData ? keywordData.keywordWithMedicines.keyword : undefined}
                                isInvalid={Boolean(errors.modifyKeyword?.message)} 
                                placeholder={"수정할 키워드 입력"} 
                                borderBottom={`1px solid ${colors.g4}`} 
                                borderTop="none" 
                                borderLeft="none" 
                                borderRight="none" 
                                borderRadius="0" 
                                _focus={{
                                    borderBottom: `1px solid ${colors.blue}`,
                                    boxShadow: "none",
                                    outline: "none"
                                }}
                            />
                            <Text fontSize={12} color={colors.g3} padding={2}>1자 이상 20자 이하로 입력해주세요.</Text>
                            {errors.modifyKeyword?.message ? <Text fontSize={12} color={colors.red} padding={2}>{errors.modifyKeyword?.message}</Text> : null}
                        </FormControl>
                    </VStack>

                    {!isKeywordDataLoading ? (
                        <Stack
                            marginTop={5}
                            width={"100%"}
                            padding={3}
                            minHeight={"450px"}
                            maxHeight="450px"
                            overflowY="auto"
                            spacing={3}
                        >
                            <Text fontSize={14} fontWeight={700} color={colors.g1} marginBottom={3}>
                                {keywordData?.keywordWithMedicines.medicines.length}개의 약품이 연결됨
                            </Text>
                            {keywordData?.keywordWithMedicines.medicines.map((medicine) => (
                                <MiniMedListItem
                                    key={medicine.id}
                                    seller={medicine.seller ?? null}
                                    medicineImage={medicine.category === "전문의약품" ? medicine.pillImage ?? null : medicine.packImage ?? null}
                                    medicineName={medicine.nameKor ?? null}
                                    packageUnit={medicine.packageUnit ?? null}
                                    ingredientInfo={medicine.ingredientInfo ?? null}
                                />
                            ))}
                        </Stack>
                    ) : (
                        <Stack
                            marginTop={5}
                            width={"100%"}
                            padding={3}
                            height={"450px"}
                            justifyContent={"center"}
                            alignItems={"center"}
                        >
                            <Spinner color={colors.blue} />
                        </Stack>
                    )}

                    <Stack marginTop={5} width={"100%"} padding={3} justifyContent={"center"} alignItems={"center"}>
                        <Button 
                            type="submit"
                            isLoading={updateKeywordMutation.isPending}
                            disabled={!isModifyKeywordValid}
                            width={"90%"}
                            backgroundColor={colors.blue}
                            color={colors.white}
                            borderRadius={10}
                            _hover={{ bg: colors.blue }}
                            boxShadow="sm"
                        >
                            수정하기
                        </Button>
                    </Stack>
                </ModalBody>
            </ModalContent>
        </Modal>    
    )
}